import { z } from 'zod';
import type { UpdateProfileRequest } from '@entities/manufacturer';
import { MAX_PRODUCTION_PHOTOS } from '@entities/media';

const EMPLOYEES = ['XS', 'S', 'M', 'L', 'XL'] as const;

export const ECO_CERTIFICATION_TYPES = ['STANDARD', 'ORGANIC', 'OTHER'] as const;

/** Mirrors the backend cap; the form stops the user before the round-trip. */
export const MAX_ECO_CERTIFICATIONS = 20;

// Spec §5.2 vs §5.3: PUT /manufacturer/profile saves any in-progress state —
// the strict "eco producer must have eco certificates" rule lives in the KYC
// submission gate (entities/manufacturer/lib/kyc.ts), not here. Enforcing it
// at form-save level used to silently block PUT when the user picked an
// ECO_PRODUCER category but hadn't uploaded certs yet.
export const profileFormSchema = z.object({
  companyName: z.string().trim().min(1, 'required').max(256),
  description: z.string().max(16000).optional().nullable(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2 (2 uppercase letters)')
    .optional()
    .nullable(),
  categories: z.array(z.string().uuid()).default([]),
  // Both country lists hold ISO-3166-1 alpha-2 codes: `exportCountries` is
  // where the company already ships, `potentialMarkets` is where it wants to.
  exportCountries: z
    .array(z.string().regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2'))
    .default([]),
  potentialMarkets: z
    .array(z.string().regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2'))
    .default([]),
  // Field names mirror the backend payload (logo/video/certificates/...).
  logo: z.string().uuid().nullable().optional(),
  video: z.string().uuid().nullable().optional(),
  certificates: z.array(z.string().uuid()).default([]),
  ecoCertificates: z.array(z.string().uuid()).default([]),
  // Structured credentials, as opposed to the scans above. The backend
  // refuses them outright for a non-eco producer, so the form hides the
  // whole block rather than letting someone fill in a 400.
  ecoCertifications: z
    .array(
      z.object({
        type: z.enum(ECO_CERTIFICATION_TYPES),
        name: z.string().trim().min(1, 'required').max(128),
        issuer: z.string().max(256).optional().nullable(),
        number: z.string().max(64).optional().nullable(),
        // Plain ISO date from a date input; empty string means "not stated".
        validUntil: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
          .or(z.literal(''))
          .optional()
          .nullable(),
        mediaId: z.string().uuid().optional().nullable(),
      }),
    )
    .max(MAX_ECO_CERTIFICATIONS, `up to ${MAX_ECO_CERTIFICATIONS} entries`)
    .default([]),
  productionPhotos: z
    .array(z.string().uuid())
    .max(MAX_PRODUCTION_PHOTOS, `up to ${MAX_PRODUCTION_PHOTOS} photos`)
    .default([]),
  ecoProducer: z.boolean().default(false),
  website: z
    .string()
    .trim()
    .url('must be a valid URL')
    .or(z.literal(''))
    .optional()
    .nullable(),
  foundedYear: z
    .number({ invalid_type_error: 'number expected' })
    .int()
    .min(0)
    .max(2100)
    .nullable()
    .optional(),
  employeesRange: z.enum(EMPLOYEES).nullable().optional(),
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;

/**
 * Backend reports validation failures using PUT-body field names, which are
 * asymmetric with the form's own names for media (`productionPhotoMediaIds`
 * vs `productionPhotos`). This maps wire name → form field so a 400 lands
 * under the right input instead of in the generic banner.
 */
export const PROFILE_FIELD_MAP: Record<string, keyof ProfileFormValues> = {
  companyName: 'companyName',
  description: 'description',
  country: 'country',
  categories: 'categories',
  exportCountries: 'exportCountries',
  potentialMarkets: 'potentialMarkets',
  logoMediaId: 'logo',
  videoMediaId: 'video',
  certificateMediaIds: 'certificates',
  ecoCertificateMediaIds: 'ecoCertificates',
  ecoCertifications: 'ecoCertifications',
  productionPhotoMediaIds: 'productionPhotos',
  ecoProducer: 'ecoProducer',
  website: 'website',
  foundedYear: 'foundedYear',
  employeesRange: 'employeesRange',
};

export function toUpdateRequest(values: ProfileFormValues): UpdateProfileRequest {
  return {
    companyName: values.companyName.trim(),
    description: values.description?.trim() || null,
    country: values.country || null,
    categories: values.categories,
    exportCountries: values.exportCountries,
    potentialMarkets: values.potentialMarkets,
    // Asymmetric API: PUT expects *MediaId/*MediaIds suffixes even though
    // the GET response uses bare names. See entities/manufacturer types.
    logoMediaId: values.logo ?? null,
    videoMediaId: values.video ?? null,
    certificateMediaIds: values.certificates,
    ecoCertificateMediaIds: values.ecoCertificates,
    // Dropped entirely when the eco flag is off: the backend rejects the
    // pair, and a leftover row from before the toggle was unticked would
    // fail the save with an error about a block the user cannot see.
    ecoCertifications: values.ecoProducer
      ? values.ecoCertifications.map((c) => ({
          type: c.type,
          name: c.name.trim(),
          issuer: c.issuer?.trim() || null,
          number: c.number?.trim() || null,
          validUntil: c.validUntil || null,
          mediaId: c.mediaId ?? null,
        }))
      : [],
    productionPhotoMediaIds: values.productionPhotos,
    ecoProducer: values.ecoProducer,
    website: values.website?.trim() || null,
    foundedYear: values.foundedYear ?? null,
    employeesRange: values.employeesRange ?? null,
  };
}
